import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BuildingView, CompanyView, OpportunityCompanyRoleView } from '@api-proxies';
import { BuildingService } from '@http/clients/building.service';
import { CompanyService } from '@http/clients/company.service';
import { EntityTypeCustomFieldService } from '@http/clients/entity-type-custom-field.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { UserService } from '@http/clients/user.service';
import { CompanyPositions } from '@models/enums/CompanyPositions';
import { EntityTypes } from '@models/enums/EntityTypes';
import { TabNames } from '@models/enums/TabNames';
import { Opportunity } from '@models/Opportunity';
import { OpportunityCompany } from '@models/OpportunityCompany';
import { SUPPORTED_OPPORTUNITY_COMPANY_ROLE_IDS } from '@models/OpportunityCompanyRoleIds';
import { OpportunityUser } from '@models/OpportunityUser';
import { AwaiterService } from '@services/awaiter.service';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { OpportunitiesSingleOverviewComponent } from '../opportunities-single/opportunities-single-overview/opportunities-single-overview.component';

@Component({
    selector: 'app-opportunities-create',
    templateUrl: './opportunities-create.component.html',
    styleUrls: ['./opportunities-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SectionHeaderComponent, OpportunitiesSingleOverviewComponent]
})
export class OpportunitiesCreateComponent implements OnInit {
    @ViewChild('overview')
    overview?: OpportunitiesSingleOverviewComponent;

    opportunity = new Opportunity();

    private readonly opportunityService = inject(OpportunityService);
    private readonly entityTypeCustomFieldService = inject(EntityTypeCustomFieldService);
    private readonly companyService = inject(CompanyService);
    private readonly buildingService = inject(BuildingService);
    private readonly awaiter = inject(AwaiterService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly modalService = inject(ModalService);
    private readonly userService = inject(UserService);

    async ngOnInit(): Promise<void> {
        await this.awaiter.awaitAction('Loading Opportunity Information', async () => {
            const newOpportunity = new Opportunity();
            this.opportunity = newOpportunity;
            this.opportunityService.setOpportunity(newOpportunity);

            const opportunitiesCustomFields = await this.entityTypeCustomFieldService.getCustomFieldsByEntityType(
                EntityTypes.Opportunity
            );
            if (opportunitiesCustomFields.length) {
                newOpportunity.customFieldValues = opportunitiesCustomFields;
            }

            const currentUser = await this.userService.getAuthenticatedUser();
            newOpportunity.opportunitySalespersons = [OpportunityUser.fromUserView(currentUser)];
            await this.prepopulateFromCompany(newOpportunity);
            await this.prepopulateFromBuilding(newOpportunity);
            this.opportunity = Opportunity.fromInterface(newOpportunity);
            this.opportunityService.setOpportunity(this.opportunity);
        });
    }

    async saveOpportunity(): Promise<void> {
        const areAllCardsValid = this.overview?.validate();
        if (areAllCardsValid) {
            await this.awaiter.awaitAction('Saving Opportunity', async () => {
                const opportunityToSave = this.overview ? this.overview.opportunity() : this.opportunity;
                this.mapCustomFieldsRawValue(opportunityToSave);
                const savedOpportunity = await this.opportunityService.save(opportunityToSave);
                await this.opportunityService.loadAvailableActions(savedOpportunity.id);
                this.opportunityService.setOpportunity(savedOpportunity);
                this.snackbarNotificationService.success('Opportunity created successfully.');
                this.router.navigate(['opportunities-single', savedOpportunity.id, TabNames[TabNames.Overview]]);
            });
        }
    }

    async cancel(): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Cancel Editing',
            cancelCaption: 'Continue Creation',
            content: 'Are you sure you want to exit? Your changes will be lost',
            title: 'Cancel Opportunity'
        });

        if (responseOk) this.router.navigate(['/opportunities']);
    }

    private async prepopulateFromCompany(newOpportunity: Opportunity): Promise<void> {
        const queryParamMap = this.route.snapshot.queryParamMap;
        const companyId = this.parsePositiveIntegerQueryParam(queryParamMap.get('companyId'));
        const roleIds = this.parseCompanyRoleIds(queryParamMap.get('companyRoleIds'));

        if (!companyId || !roleIds.length) return;

        let company: CompanyView;
        try {
            company = await this.companyService.getById(companyId);
        } catch {
            // Skip prepopulation on failure; the interceptor handles auth and not-found redirects.
            return;
        }

        const nonCustomerRoleIds = roleIds.filter((roleId) => roleId !== CompanyPositions.Customer);

        if (roleIds.includes(CompanyPositions.Customer)) {
            newOpportunity.companyId = company.id;
        }

        if (nonCustomerRoleIds.length) {
            const opportunityCompany = OpportunityCompany.fromCompanyView(company);
            opportunityCompany.opportunityCompanyRoles = nonCustomerRoleIds.map(
                (roleId) => ({ buildingContactRoleId: roleId }) as OpportunityCompanyRoleView
            );
            newOpportunity.opportunityCompanies = [...(newOpportunity.opportunityCompanies ?? []), opportunityCompany];
        }
    }

    private async prepopulateFromBuilding(newOpportunity: Opportunity): Promise<void> {
        const buildingId = this.parsePositiveIntegerQueryParam(this.route.snapshot.queryParamMap.get('buildingId'));
        if (!buildingId) return;

        let building: BuildingView;
        try {
            building = await this.buildingService.getById(buildingId);
        } catch {
            // Skip Building prepopulation on failure; the interceptor handles auth and not-found redirects.
            return;
        }

        newOpportunity.buildingId = building.id;
        newOpportunity.line2 = building.line2;
        newOpportunity.buildingAddressId = building.addressId;
    }

    private parsePositiveIntegerQueryParam(rawValue: string | null): number | undefined {
        const value = rawValue?.trim();
        if (!value || !/^\d+$/.test(value)) return undefined;

        const parsedValue = Number(value);
        return Number.isSafeInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
    }

    private parseCompanyRoleIds(rawCompanyRoleIds: string | null): number[] {
        if (!rawCompanyRoleIds) return [];

        const roleIds: number[] = [];
        const seenRoleIds = new Set<number>();

        for (const rawRoleId of rawCompanyRoleIds.split(',')) {
            const trimmedRoleId = rawRoleId.trim();
            if (!trimmedRoleId || !/^\d+$/.test(trimmedRoleId)) continue;

            const parsedRoleId = Number(trimmedRoleId);
            if (!Number.isSafeInteger(parsedRoleId) || parsedRoleId <= 0) continue;
            if (!SUPPORTED_OPPORTUNITY_COMPANY_ROLE_IDS.includes(parsedRoleId)) continue;
            if (seenRoleIds.has(parsedRoleId)) continue;

            seenRoleIds.add(parsedRoleId);
            roleIds.push(parsedRoleId);
        }

        return roleIds;
    }

    private mapCustomFieldsRawValue(opportunity: Opportunity): void {
        if (!opportunity.customFieldValues?.length) return;

        opportunity.customFieldValues.forEach((customField) => {
            const raw = customField.rawValue;

            if (raw === undefined || raw === null || raw === '') {
                customField.value = undefined;
                return;
            }

            if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
                customField.value = raw.toString();
                return;
            }

            try {
                customField.value = JSON.stringify(raw);
            } catch {
                customField.value = (raw as unknown as { toString?: () => string })?.toString?.();
            }
        });
    }
}
